// Shared visual tokens for the Nutrition Progress content views (Table, Calendar,
// later Graphs). Extracted from TableView so every view maps the same adherence
// tokens and macro identities to the same colors. Scaffold palette on a light
// background — the real (dark) design replaces these later; the COLORS just
// encode adherence/identity so the numbers stay verifiable.

import moment from "moment";

import { convertUnit } from "../../../../../utils/converters/convert-units";

// Canonical YYYYMMDD-number → display-string formatter for these views (Table,
// Calendar). Both used to reimplement this locally with slightly different
// signatures (moment(String(utcDate)) vs moment(String(utcDate), "YYYYMMDD"));
// consolidated here so date formatting stays consistent across views.
export const fmtUtc = (utcNum, fmt = "MMM D") =>
  moment(String(utcNum), "YYYYMMDD").format(fmt);

// ms → "Xh Ym" (daily-stats.sleep convention). Consolidated here — TableView
// and MetricCards each had their own slightly-different local copy.
export const fmtSleep = (ms) => {
  if (ms == null) return "—";
  const totalMin = Math.round(ms / 60000);
  return `${Math.floor(totalMin / 60)}h ${String(totalMin % 60).padStart(2, "0")}m`;
};

// Meal-type sort order for the Food Log / Meal Photos cards (Day Drawer +
// Table) — was a byte-identical MEAL_ORDER array + indexOf-with-fallback
// comparator copy-pasted into 4 files. mealOrderIndex works against either a
// plain meal-type string (Meal Photos' groupByMealType) or a meal object's
// own .name (Food Log's sortMeals) — the caller picks what to compare.
export const MEAL_ORDER = ["Breakfast", "Lunch", "Dinner", "Snack"];
export const mealOrderIndex = (name) => {
  const i = MEAL_ORDER.indexOf(name);
  return i === -1 ? MEAL_ORDER.length : i;
};

// Translation helper for the CalendarHeader card (range title, phase bands,
// plan timeline) — shared by CalendarHeader.js and CalendarTimelines.js.
export const translateCalendarHeader = (t, key, opts) =>
  t(`nutritionPage.progress.calendarHeader.${key}`, opts);

// Translation helper for the Calendar legend bar (title, adherence swatches,
// workout/photo/period icons) — shared by CalendarLegend.js and CalCell.js
// (the day cell reuses the "notLogged" label verbatim).
export const translateCalendarLegend = (t, key, opts) =>
  t(`nutritionPage.progress.calendarLegend.${key}`, opts);

// Translation helper for the Calendar week grid itself (week-avg column,
// new-plan marker, logged counts) — shared by CalendarView.js and CalCell.js.
export const translateCalendarGrid = (t, key, opts) =>
  t(`nutritionPage.progress.calendarGrid.${key}`, opts);

// Translation helper for the Log view (week-average card, week nav, day-head
// TODAY badge) — shared by LogWeekAverage.js, LogWeekNav.js and LogGrid.js.
export const translateLog = (t, key, opts) =>
  t(`nutritionPage.progress.log.${key}`, opts);

// Translation helper for the Day Drawer (badges, target box, chip labels,
// tabs, empty states) — shared by DayDrawer.js and DayDrawerHeader.js.
export const translateDayDrawer = (t, key, opts) =>
  t(`nutritionPage.progress.dayDrawer.${key}`, opts);

// Working TDEE warning state — staleData takes priority over
// decoupled (no recent data at all makes the weight-swing warning, which is
// about interpreting a RECENT trend, moot). Single source of truth shared by
// TdeeCard (SummaryHero.js), HeroChart (SummaryHeroChart.js) and
// ReportsIntakeChart.js — all 3 render the same warning, just in different
// shapes (card text vs. chart reference-line color/label).
export const tdeeWarningState = ({ staleData, decoupled, weeklyChange }, t) => {
  if (staleData) {
    return { isWarn: true, warningText: t("nutritionPage.progress.hero.staleDataWarn") };
  }
  if (decoupled) {
    return {
      isWarn: true,
      warningText: t("nutritionPage.progress.hero.decoupledWarn", { change: Math.abs(weeklyChange ?? 0) }),
    };
  }
  return { isWarn: false, warningText: null };
};

// Adherence status colors (good/warn/bad/none) for the Calendar grid — single
// source of truth shared by CalendarLegend's swatches and the day-cell/avg
// KcalChips, so the legend always matches what the cells actually render.
// Every `text` is a CSS custom property (--gradient-right/--sore already
// existed app-wide; --adh-warn/--adh-none are new, variables.css, since
// nothing else in the app already used those exact colors) — the single
// place these 4 colors are defined. `bg` derives a ~12% tint of `text` via
// color-mix() (same technique as news.css/scheduling.css) instead of a
// hand-kept-in-sync rgba() duplicate of the same RGB triplet.
export const ADH_COLOR = {
  good: { text: "var(--gradient-right)", bg: "color-mix(in srgb, var(--gradient-right) 12%, transparent)" },
  warn: { text: "var(--adh-warn)", bg: "color-mix(in srgb, var(--adh-warn) 12%, transparent)" },
  bad:  { text: "var(--sore)", bg: "color-mix(in srgb, var(--sore) 12%, transparent)" },
  none: { text: "var(--adh-none)", bg: "color-mix(in srgb, var(--adh-none) 12%, transparent)" },
};

// Bodyweight is always stored/computed in kg (see TableView/HeroChart convention) —
// only the display layer converts to lb. Works for absolute values AND signed
// deltas/rates alike, since kg↔lb is a pure linear scale (sign is preserved).
export const toDisplayWeight = (kgValue, isImperial) => {
  if (kgValue == null) return null;
  const converted = isImperial ? convertUnit(kgValue, "kg", "lb") : kgValue;
  return Math.round(converted * 10) / 10;
};

// Weekly rates (kg/week -> display unit/week) keep two decimals where
// toDisplayWeight keeps one, so a 0.18 kg/wk pace never reads as 0.2.
export const toDisplayRate = (kgPerWeek, isImperial) => {
  if (kgPerWeek == null) return null;
  const converted = isImperial ? convertUnit(kgPerWeek, "kg", "lb") : kgPerWeek;
  return Math.round(converted * 100) / 100;
};

// Inverse of toDisplayWeight — a coach-entered value in their preferred unit,
// converted back to kg (the only unit ever stored/computed). Was duplicated
// verbatim in BodyweightGoal.js and GoalAdjustModal.js; consolidated here.
export const toKg = (displayValue, isImperial) =>
  (isImperial ? Math.round(convertUnit(displayValue, "lb", "kg") * 10) / 10 : displayValue);

// X-axis tick indices spread evenly across [0, lastIdx], always including
// both ends — extracted from SummaryHeroChart.js's HeroChart (now shared with
// Reports' own line charts). Interpolated between 0 and lastIdx rather than
// stepped forward from 0, so it never undershoots the end on a series length
// that isn't a clean multiple of `count`.
export const evenlySpacedTicks = (lastIdx, count) => {
  if (lastIdx <= 0) return lastIdx === 0 ? [0] : [];
  const n = Math.min(count, lastIdx + 1);
  const ticks = [];
  for (let i = 0; i < n; i++) ticks.push(Math.round((i * lastIdx) / (n - 1)));
  return ticks;
};

// Marks which items in an ordered-by-x list get a visible label without any
// two visible labels colliding — a single pass comparing each candidate's
// real pixel x-position against the last label actually shown (not just its
// immediate neighbor), so a dense run of close items collapses onto whichever
// ones DO have enough room, instead of every close pair fighting pairwise.
// Built for HeroChart's training-phase transition markers (SummaryHero.js) —
// see that component's own comment for the full "why". Falls back to
// showing every label when containerWidth/totalDays aren't known yet (e.g.
// the very first render, before the chart's container has mounted).
export const withoutLabelCollisions = (items, { xKey, containerWidth, totalDays, minGapPx = 60 }) => {
  if (!containerWidth || !totalDays) return items.map((item) => ({ ...item, showLabel: true }));
  const pxPerUnit = containerWidth / totalDays;
  let lastShownPx = -Infinity;
  return items.map((item) => {
    const px = item[xKey] * pxPerUnit;
    const showLabel = px - lastShownPx >= minGapPx;
    if (showLabel) lastShownPx = px;
    return { ...item, showLabel };
  });
};

// Macro identity — order/keys mirror MACROS from the engine (dayData.js).
// Labels/units are translated (not a static lookup) — shared by Table
// (TableHead.js) and Calendar (CalCell.js), the two views with per-macro text.
export const translateMacroLabel = (t, key) => t(`nutritionPage.progress.macros.${key}`);
export const translateMacroUnit = (t, key) => t(`nutritionPage.progress.macroUnits.${key}`);

export const MACRO_COLORS = {
  proteins: "var(--protein)",
  carbs: "var(--carbs)",
  fats: "var(--fat)",
  fibers: "#46c69a",
  kcal: "#a99bf0",
};

// Shared Recharts chrome colors (grid lines, axis ticks, hover cursor) for
// every chart in Hero/Reports — theme-aware var(--white-XX) tokens, not
// hardcoded rgba(255,255,255,X): those only read correctly on a fixed-dark
// background, but these cards follow the app's real light/dark toggle (see
// SummaryHero.js's original CHART_TICK/GRID/CURSOR comment for the full
// "why"). Was 4 near-identical const blocks (Hero + 3 Reports charts);
// collapsed here so changing the chart chrome palette is a one-line edit.
// lineCursor (stroke) is for line/composed charts' vertical hover guide;
// barCursor (fill) is for bar charts' highlighted-column hover — different
// CSS property, so kept as two distinct values rather than one shared one.
export const CHART_THEME = {
  tick: "var(--white-70)",
  tickFontSize: 12,
  grid: "var(--white-05)",
  lineCursor: "var(--white-40)",
  barCursor: "var(--white-05)",
};

// Y-axis auto-zoom for the kcal axis of HeroChart/ReportsIntakeChart. Recharts'
// default domain is always 0-anchored, which wastes most of the chart height
// when a well-behaved client's data (+ the TDEE reference line) clusters near
// the top. Percentile-trimmed min/max (ignores the most extreme 5% on each
// end) + proportional padding zooms into the real cluster — but a genuine
// outlier day (e.g. a 20,000 kcal logging error) makes the RAW range balloon
// far past the TRIMMED range; zooming around the trimmed range in that case
// would clip the outlier off-chart silently. When that divergence exceeds
// outlierRatio, bail out to null (caller falls back to Recharts' own default)
// instead of guessing — better to show the "wasted space" everyone already
// understands than a domain that hides real data.
export const computeAxisDomain = (
  values,
  {
    mustInclude = [],
    trimPercentile = 0.05,
    paddingPct = 0.12,
    // Asymmetric override for paddingPct — e.g. BodyweightTrendCard wants
    // more headroom below than above (per product lead's requested 15%
    // above / 30% below) instead of this function's usual symmetric pad.
    // Each defaults to paddingPct, so callers that don't pass these get the
    // exact same symmetric behavior as before.
    paddingAbovePct = paddingPct,
    paddingBelowPct = paddingPct,
    minSpan = 200,
    floorAt = 0,
    outlierRatio = 2.5,
    roundTo = 10,
  } = {}
) => {
  const nums = values.filter((v) => v != null && Number.isFinite(v));
  if (nums.length < 4) return null;

  const sorted = [...nums].sort((a, b) => a - b);
  const rawMin = sorted[0];
  const rawMax = sorted.at(-1);
  const rawSpan = rawMax - rawMin;
  if (rawSpan === 0) return null;

  // Math.floor(sorted.length * trimPercentile) rounds to 0 below ~20 points
  // (e.g. a 14-day quick-select range), which would skip trimming entirely
  // and let a single outlier day slip through the ratio check below
  // undetected. Force at least 1 point trimmed off each end once there's
  // enough data to do so safely (8+ points, leaving 4+ after trimming).
  const minTrim = sorted.length >= 8 ? 1 : 0;
  const trimCount = Math.max(minTrim, Math.min(Math.floor(sorted.length / 2) - 2, Math.round(sorted.length * trimPercentile)));
  const trimmed = trimCount > 0 ? sorted.slice(trimCount, sorted.length - trimCount) : sorted;
  const trimmedMin = trimmed[0];
  const trimmedMax = trimmed.at(-1);
  const trimmedSpan = trimmedMax - trimmedMin;
  if (trimmedSpan === 0) return null;

  if (rawSpan / trimmedSpan > outlierRatio) return null;

  let padBelow = trimmedSpan * paddingBelowPct;
  let padAbove = trimmedSpan * paddingAbovePct;

  // Expanding to minSpan by just re-centering (old behavior) silently wiped
  // out asymmetric padding whenever the real data span was small enough to
  // need it — exactly the common case for a low-variance metric like
  // bodyweight, where this bit never visibly ran. Split the extra space
  // needed in the SAME above:below ratio as the padding itself instead, so
  // asymmetry survives. When paddingAbovePct === paddingBelowPct this is
  // mathematically identical to the old center-on-midpoint behavior (every
  // other caller, unaffected).
  const totalPad = padAbove + padBelow;
  const domainSpan = trimmedSpan + totalPad;
  if (domainSpan < minSpan) {
    const deficit = minSpan - domainSpan;
    const aboveRatio = totalPad > 0 ? padAbove / totalPad : 0.5;
    padAbove += deficit * aboveRatio;
    padBelow += deficit * (1 - aboveRatio);
  }

  let domainMin = trimmedMin - padBelow;
  let domainMax = trimmedMax + padAbove;

  // mustInclude (e.g. the TDEE reference line) is a single fixed value, not
  // noisy data — it must never get caught by the trim above. If it were
  // folded into `values` and happened to be the single most extreme point,
  // trimming would exclude it from the padding math while Recharts still
  // draws it at its real y — leaving it flush against the domain edge with
  // no room for its label. Expand (never shrink) the domain to keep it
  // comfortably inside, using half the main padding on each side. Average
  // of above/below when they differ — mustInclude callers (kcal/TDEE) don't
  // use asymmetric padding today, so this is just the plain paddingPct for
  // them; it's only here so asymmetric callers wouldn't get a wrong value
  // if they ever combine the two.
  const refPad = (domainMax - domainMin) * ((paddingAbovePct + paddingBelowPct) / 2 / 2);
  for (const ref of mustInclude) {
    if (ref == null || !Number.isFinite(ref)) continue;
    if (ref + refPad > domainMax) domainMax = ref + refPad;
    if (ref - refPad < domainMin) domainMin = ref - refPad;
  }

  // The trim above deliberately excludes 1+ extreme real points from the
  // padding math to avoid "wasted space" around a tight cluster — but every
  // caller here also sets allowDataOverflow (so this zoomed domain is
  // actually enforced, not just a hint Recharts may expand). A trimmed
  // point that ISN'T extreme enough to trip the outlierRatio bailout above
  // still gets rendered at its real value by Recharts, which can land
  // outside [domainMin, domainMax] — invisible, clipped by the plot's own
  // clip-path, not "connected" through a gap the way a null value would be
  // (caught on BodyweightTrendCard.js's longer windows, where a handful of
  // real weigh-ins over months is exactly the sparse-cluster-plus-one-dip
  // shape that trips this: e.g. domain [72,82] over data spanning 65-81 —
  // real, logged weigh-ins are never dropped from a chart meant to show
  // that person's weigh-ins, so the domain must cover them even if that
  // costs back some of the "zoom"). Only widens, never shrinks.
  domainMin = Math.min(domainMin, rawMin);
  domainMax = Math.max(domainMax, rawMax);

  if (floorAt != null) domainMin = Math.max(domainMin, floorAt);

  // Snaps the edges to a "nice" grid so ticks land on round numbers — but
  // the grid size has to scale with the axis, not stay fixed. The default
  // (10) is sized for kcal, where a domain typically spans hundreds/
  // thousands and rounding to the nearest 10 barely moves it. A bodyweight
  // axis spans maybe 10-20 units total — rounding THAT to the nearest 10
  // was clobbering more than half the padding just computed, shifting the
  // real data off-center instead of merely tidying the tick labels (caught
  // via fixtures: a real 75-80kg cluster came out as domain [70, 80], with
  // the actual weigh-ins pinned to the very top edge). Callers on a smaller
  // scale (weight) pass a smaller roundTo.
  //
  // floor()/ceil() here, not round() — rounding to "nearest" can move an
  // edge INWARD by up to half of roundTo (e.g. a real 65.6 min, roundTo=1:
  // round(65.6)=66, now above the real value), silently reintroducing the
  // exact clipping the domainMin/domainMax guarantee above exists to
  // prevent (allowDataOverflow is true on every caller's axis, so a value
  // outside the returned domain renders off-canvas, not just off-tick).
  // Snapping outward only can only widen the domain, never re-narrow it.
  return [Math.floor(domainMin / roundTo) * roundTo, Math.ceil(domainMax / roundTo) * roundTo];
};

// kcal-axis domain for the intake-vs-bodyweight charts (SummaryHeroChart +
// ReportsIntakeChart — identical setup). Both plotted kcal lines (intake, a
// 7-day rolling average, and target) plus the TDEE reference line must fit
// inside the axis, so their actual extremes go in mustInclude: that leaves
// computeAxisDomain's outlier trim governing only the PADDING, never clipping
// a line that's actually drawn. Without it, allowDataOverflow lets Recharts
// render off-domain points and the SVG clips them flat at the top edge (the
// Consumed line "cut off" bug). The rolling intake line has no single-day
// spikes for the trim to legitimately suppress anyway — its peaks are real
// multi-day signal (a refeed / overshoot period).
export const kcalChartDomain = (series, workingExpenditure) => {
  const kcalVals = series
    .flatMap((p) => [p.intake, p.target])
    .filter((v) => v != null && Number.isFinite(v));
  const mustInclude = [workingExpenditure];
  if (kcalVals.length) mustInclude.push(Math.min(...kcalVals), Math.max(...kcalVals));
  return computeAxisDomain(series.flatMap((p) => [p.intake, p.target]), { mustInclude });
};

// Every weigh-in is plotted, so the trim in computeAxisDomain must never clip one off-chart: force the real min/max in.
export const weightChartDomain = (weights) => {
  const vals = weights.filter((v) => v != null && Number.isFinite(v));
  const mustInclude = vals.length ? [Math.min(...vals), Math.max(...vals)] : [];
  return computeAxisDomain(weights, { minSpan: 10, roundTo: 1, mustInclude });
};

// Adherence token → a basic, light-bg-readable text color.
export const TOKEN_COLOR = {
  good: "#1a7f4b",
  warn: "#b9770e",
  bad: "#c0392b",
  none: "#9aa0a6",
};

// Adherence token → solid status chip (tinted fill + strong text), used by the
// Calendar kcal chip and week/weekday averages.
export const CHIP_BG = {
  good: "#d6f0e0",
  warn: "#fbe7c2",
  bad: "#f6d4cf",
  none: "#eceef0",
};
export const CHIP_TEXT = TOKEN_COLOR;

export const MUTED = "#9aa0a6";

// Goal state chip colors — same yellow/green/red used across the insights
// panel. Keep in sync with .np-insights__row--info/--warn/--good's
// --insight-bg/--insight-icon in nutrition.css (CSS custom properties can't
// be read from JS, so this is the single JS-side source of truth).
export const GOAL_STATE_COLORS = {
  good: { text: "var(--gradient-right)", bg: "rgba(91, 197, 0, 0.1)" },
  warn: { text: "var(--energy)", bg: "rgba(247, 182, 0, 0.1)" },
  bad:  { text: "var(--sore)", bg: "rgba(255, 56, 82, 0.1)" },
  none: { text: "var(--white-50)", bg: "var(--white-05)" },
};

// "Customise Table" stat keys — grouped into the 2 columns shown in the
// "Select Stats" modal. All 12 have a real column in TableView.js except
// "trainingReadiness" — the schema only has 5 sub-scores (sore/stress/
// energy/mood/sleep), not a single number, so it's still an open product
// decision how to reduce that to one column; kept here so a saved template
// referencing it is forward-compatible once that lands.
// Mirrored in msb-api/lib/schemas/nutrition-table-template.js (separate
// service, can't import this directly) — keep both in sync.
export const TABLE_STAT_GROUPS = [
  ["target", "consumed", "adherence", "bodyweight", "steps", "sleep", "days"],
  ["hrv", "rhr", "trainingReadiness", "sessionRPE", "waterIntake", "bodyFatPercent"],
];

export const ALL_TABLE_STAT_KEYS = TABLE_STAT_GROUPS.flat();
